import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { Button, Link, Icon, DatePicker } from '@/ui'
import { TextColors } from '@/styles'

import { faArrowLeft, faDownload } from '@fortawesome/pro-regular-svg-icons'
import { useSearchParams } from 'react-router-dom'
import { DateTime } from 'luxon'

import {
  DEFAULT_START_DATE,
  DEFAULT_END_DATE,
} from '@/constants/businessConstants'
import {
  Expense,
  fetchExpensesBreakdown,
  fetchOtherBreakdown,
  fetchProfitsBreakdown,
  fetchUncategorizedBreakdown,
} from '@/actions/reportActions'
import { fetchTransactionCategoriesIfNeeded } from './reports.slice'
import { transactionFilterToQueryUrlService } from '@/services/transactionFilterToQueryUrlService'
import { MAX_TRANSACTION_LIMIT } from '@/constants/transactionConstants'
import { getCurrentUser } from '@/selectors/user.selectors'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'

import { PageHeader } from '@/components/PageHeader'
import { PdfWrapper, PdfWrapperHandle } from '@/components/PdfWrapper'
import ProfitAndLossPDF from './ProfitAndLossPDF'
import { FreeTrialUnreviewedAlert } from '@/components/FreeTrialUnreviewedAlert'
import { ProfitAndLossReports } from './ProfitAndLossReports/index'
import { fetchFilteredUserTransactions } from '@/features/transactions/transactions.slice'

// TODO: Move to v2
// import { DatePicker } from '@/ui'

export const ProfitAndLossPanel = () => {
  const dispatch = useAppDispatch()
  const pdfRef = useRef<PdfWrapperHandle>(null)

  const currentUser = useReselector(getCurrentUser)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)

  const [searchParams] = useSearchParams()

  const [profitsObj, setProfitsObj] = useState<{
    profits: Expense[]
    sum: number | string
  } | null>(null)
  const [expensesObj, setExpensesObj] = useState<{
    expenses: Expense[]
    sum: number | string
  } | null>(null)
  const [otherObj, setOtherObj] = useState<{
    otherExpenses: Expense[]
    sum: number | string
  } | null>(null)
  const [uncategorizedObj, setUncategorizedObj] = useState<{
    sum: number
    transactionCategoryId: number | null
  } | null>(null)
  const [fetchingData, setFetchingData] = useState(true)
  const [startDate, setStartDate] = useState(DEFAULT_START_DATE)
  const [endDate, setEndDate] = useState(DEFAULT_END_DATE)

  useEffect(() => {
    dispatch(fetchTransactionCategoriesIfNeeded())
  }, [dispatch])

  const handleDateRangeChange = useCallback(
    ([startDate, endDate]: [string, string]) => {
      setStartDate(startDate)
      setEndDate(endDate)
    },
    []
  )

  useEffect(() => {
    const startDate = searchParams.get('startDate')
    const endDate = searchParams.get('endDate')
    if (startDate) {
      setStartDate(startDate)
    }
    if (endDate) {
      setEndDate(endDate)
    }
  }, [searchParams])

  useEffect(() => {
    const fetchData = async () => {
      setFetchingData(true)
      // const { startDate, endDate } = this.state
      // Default query to fetch all if there is no start or end date
      const query = {
        year: !startDate || !endDate ? 'all' : undefined,
        startDate:
          startDate && endDate
            ? DateTime.fromFormat(startDate, DATE_FORMATS_LUXON.INPUT)
            : undefined,
        endDate:
          startDate && endDate
            ? DateTime.fromFormat(endDate, DATE_FORMATS_LUXON.INPUT)
            : undefined,
      }

      const [profits, expenses, uncategorized, other] = await Promise.all([
        dispatch(fetchProfitsBreakdown(query)),
        dispatch(fetchExpensesBreakdown(query)),
        dispatch(fetchUncategorizedBreakdown(query)),
        dispatch(fetchOtherBreakdown(query)),
        currentUser?.id &&
          fetchFilteredUserTransactions(
            transactionFilterToQueryUrlService({
              startDate: startDate ?? '',
              endDate: endDate ?? '',
              limit: MAX_TRANSACTION_LIMIT,
              userId: currentUser.id,
            })
          )(dispatch),
      ])
      setProfitsObj(profits)
      setExpensesObj(expenses)
      setUncategorizedObj(uncategorized)
      setOtherObj(other)
      setFetchingData(false)
    }
    fetchData()
  }, [dispatch, endDate, startDate, currentUser?.id])

  const fromInsights = useMemo(() => {
    return searchParams.get('from') === 'insights'
  }, [searchParams])

  return (
    <>
      {fromInsights && (
        <Link
          href="/insights/overview"
          newPage={false}
          iconLeft={faArrowLeft}
          variant="primary"
          color={TextColors.BRAND}
        >
          Back to Practice Insights
        </Link>
      )}
      <PageHeader header="Profit and Loss Statement" className="pt-4 pb-2" />
      <div className="statementTitle flex flex-col gap-4 mx-5">
        <div className="flex flex-wrap items-center justify-between gap-4 md:gap-2">
          <div className="flex-grow md:flex-none md:w-3/10">
            <DatePicker
              clearable
              value={[startDate, endDate]}
              onChange={handleDateRangeChange}
              selectsRange
              startDate={startDate}
              endDate={endDate}
              fullWidth
            />
          </div>
          {!fetchingData && profitsObj && (
            <div className="flex-grow md:flex-none md:w-3/10 md:ml-auto w-full">
              <Button
                variant="secondary"
                onClick={pdfRef.current?.generatePDF}
                className="w-full"
                loading={fetchingData}
                size="lg"
              >
                <Icon icon={faDownload} className="mr-2" />
                {isMobile ? 'Download' : 'Download Statement'}
              </Button>
            </div>
          )}
        </div>

        <FreeTrialUnreviewedAlert />
        <br />
        <ProfitAndLossReports
          startDate={startDate}
          endDate={endDate}
          profitsObj={profitsObj}
          expensesObj={expensesObj}
          otherObj={otherObj}
          fetchingData={fetchingData}
          uncategorizedObj={uncategorizedObj}
          userIdForAdmin={null}
        />
      </div>

      <PdfWrapper
        filename={`profit_and_loss_${currentUser?.firstName}_${currentUser?.lastName}.pdf`}
        ref={pdfRef}
        hideComponent
        hideMargin
        footerText={`Created ${DateTime.now().toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)}`}
      >
        <ProfitAndLossPDF
          expensesObj={expensesObj}
          profitsObj={profitsObj}
          otherObj={otherObj}
          startDate={startDate}
          endDate={endDate}
        />
      </PdfWrapper>
    </>
  )
}
